import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Country, State, City } from "country-state-city";
import ReactSlider from "react-slider";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../context";
import toast from "react-hot-toast";
import "../App.css";

export default function HomePage() {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [gender, setGender] = useState("");
  const [lookingFor, setLookingFor] = useState("");
  const [ageRange, setAgeRange] = useState([18, 45]);

  const navigate = useNavigate();
  const { user, fetchAllUsers  } = useAuthContext();

  const isLoggedIn = !!localStorage.getItem("token");

  const isGold = user?.membership_type === "gold" && user?.membership_status === true;

  const [featuredGoldUsers, setfeaturedGoldUsers] = useState([]);

  useEffect(() => {
    const loadGoldUsers = async () => {
      const allUsers = await fetchAllUsers();
      const gold = allUsers.filter(
        (u) =>
          u.membership_type === "gold" &&
          u.membership_status === true &&
          u.user_id !== user?.user_id // Exclude current user
      );
      setfeaturedGoldUsers(gold.slice(0, 3));
    };
  
    loadGoldUsers();
  }, [fetchAllUsers, user]);  

  // load all countries once
  useEffect(() => {
    setCountries(Country.getAllCountries() || []);
  }, []);

  // when country changes, load its states
  useEffect(() => {
    if (selectedCountry) {
      setStates(State.getStatesOfCountry(selectedCountry) || []);
    } else {
      setStates([]);
    }
    setSelectedState("");
    setCities([]);
    setSelectedCity("");
  }, [selectedCountry]);

  const scrollToTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  // when state changes, load only its cities
  useEffect(() => {
    if (selectedCountry && selectedState) {
      setCities(City.getCitiesOfState(selectedCountry, selectedState) || []);
    } else {
      setCities([]);
    }
    setSelectedCity("");
  }, [selectedCountry, selectedState]);

  const handleSearch = () => {
    if (!gender || !lookingFor || ageRange.length !== 2) {
      return alert(
        "Please select your gender, preference, and age range."
      );
    }

    const query = {
      ageRange,
      gender,
      lookingFor,
      ...(selectedCountry && { country: selectedCountry }),
      ...(selectedState && { state: selectedState }),
      ...(selectedCity && { city: selectedCity }),
    };

    navigate("/search", { state: query });
    scrollToTop();
  };

  return (
    <>
    <Helmet>
  <title>Home – SomeOne4u</title>
  <meta name="description" content="Find meaningful matches across the world." />
  <meta name="keywords" content="SomeOne4u, global dating, connect worldwide, find love, international relationships" />
  <meta name="author" content="SomeOne4u Team" />
  <meta name="application-name" content="SomeOne4u" />
  <meta name="language" content="EN" />
 
  <link rel="canonical" href="https://someone4u.com" />
 
  {/* Open Graph */}
  <meta property="og:title" content="SomeOne4u – Global Dating Platform" />
  <meta property="og:description" content="Discover meaningful connections on SomeOne4u, a global dating platform built for real people and true love." />
  <meta property="og:url" content="https://someone4u.com" />
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="SomeOne4u" />
  <meta property="og:image" content="https://someone4u.com/image/splash.png" />
  <meta property="og:image:alt" content="Someone4u splash image" />
  <meta property="og:locale" content="en_US" />
 
  {/* Twitter Card */}
  {/* <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="SomeOne4u – Global Dating Platform" />
  <meta name="twitter:description" content="Join SomeOne4u and discover matches that truly connect with you." />
  <meta name="twitter:image" content="https://someone4u.com/image/splash.png" /> */}
</Helmet>

      <div className="min-h-screen w-full font-sans text-gray-800">
        {/* Hero */}
        <section
        className="relative h-[80vh] bg-cover bg-center flex items-center justify-center text-center px-4 text-white"
        style={{
          backgroundImage:
            "url('https://img.freepik.com/free-photo/young-sexy-romantic-couple-love-happy-summer-beach-together-having-fun-wearing-swim-suits-showing-heart-sign-sundet_285396-6545.jpg?semt=ais_country_boost&w=740')",
        }}
      >
          <div className="absolute inset-0 bg-black opacity-40"></div>
          <div className="relative z-10 bg-black bg-opacity-40 p-6 rounded-xl max-w-xl">
            <h2 className="text-4xl font-extrabold mb-4">
              The smart way to date worldwide
            </h2>
            <p className="text-lg mb-6">
              SomeOne4u helps you find real connections that matter, wherever
              you are.
            </p>
            {!isLoggedIn && (
              <button
                className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-full text-lg shadow-md"
                onClick={() => {
                  navigate("/signup");
                  scrollToTop();
                }}
              >
                Get Started
              </button>
            )}
          </div>
        </section>

        {featuredGoldUsers.length > 0 && (
  <section className="py-16 bg-white">
    <div className="max-w-6xl mx-auto text-center px-4">
      <h2 className="text-3xl font-bold mb-12 text-gray-800">✨ Featured Singles ✨</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
        {featuredGoldUsers.map((u, idx) => (
          <div
            key={idx}
            onClick={() => {
              if (isLoggedIn) {
                navigate(`/user/${u.user_id}`);
              } else {
                toast.error("Please log in or sign up first to view profiles.");
                navigate("/login");
              }
              scrollToTop();
            }}
            className="relative cursor-pointer bg-white border border-gray-200 rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 p-6 flex flex-col items-center group"
          >
            {/* Badge */}
            <span className="absolute top-3 right-3 bg-yellow-400 text-white text-sm font-semibold px-3 py-1 rounded-full shadow">
              🌟 Gold Member
            </span>

            {/* Profile Image */}
            <div className="w-36 h-36 rounded-full overflow-hidden mb-5 border-2 border-yellow-300">
              <img
                src={u.profile_image || "/image/operator.png"}
                alt={u.firstName}
                className="w-full h-full object-cover"
              />
            </div>

            {/* User Info */}
            <h4 className="font-semibold text-lg text-gray-900">
              {u.firstName}, {u.age}
            </h4>
            <p className="text-sm text-gray-500">
              {u.city}, {u.state}, {u.country}
            </p>

            {/* Hover Button */}
            <button
              className="mt-5 opacity-90 group-hover:opacity-100 transition-opacity text-sm bg-blue-500 text-white px-5 py-2 rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                if (isLoggedIn) {
                  navigate(`/user/${u.user_id}`);
                  scrollToTop();
                } else {
                  toast.error("Please log in or sign up first to view profiles.");
                  navigate("/login");
                  scrollToTop();
                }
              }}
            >
              View Profile
            </button>
          </div>
        ))}
      </div>
    </div>
  </section>
)}

        {/* Features */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-10">
            <Feature
              icon="💬"
              title="Meaningful Matches"
              desc="Our algorithm prioritizes compatibility, not just swipes."
            />
            <Feature
              icon="🔒"
              title="Safe & Secure"
              desc="Privacy-first design with verified profiles and respectful community."
            />
            <Feature
              icon="🌍"
              title="Global Community"
              desc="Connect with singles around the world."
            />
          </div>

          {/* Gold CTA */}
          {isLoggedIn && !isGold && (
            <section className="mt-20 bg-yellow-50 rounded-lg py-12 px-6 shadow-inner">
              <div className="max-w-3xl mx-auto text-center">
                <img
                  src="/image/explore.png"
                  alt="Explore More"
                  className="mx-auto w-32 h-32 object-contain mb-6"
                />
                <h2 className="text-3xl font-extrabold text-yellow-600 mb-3">
                  Go Gold & Unlock Everything
                </h2>
                <p className="text-gray-700 mb-6 text-base">
                  View unlimited profiles, send unlimited messages, and get top
                  visibility.
                </p>
                <button
                  onClick={() => {
                    navigate("/membership");
                    scrollToTop();
                  }}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-6 py-2 rounded shadow transition"
                >
                  See Gold Benefits
                </button>
              </div>
            </section>
          )}
        </section>

        {/* Search Form */}
        <section className="bg-gray-100 py-16 px-6">
          <div className="max-w-md mx-auto text-center">
            <h3 className="text-xl font-semibold text-[#46666d] mb-8">
              Singles searching for love near you
            </h3>

            <Selector
              label="I'm a"
              options={["Woman", "Man"]}
              selected={gender}
              onSelect={setGender}
            />
            <Selector
              label="Looking for a"
              options={["Woman", "Man"]}
              selected={lookingFor}
              onSelect={setLookingFor}
            />

            <div className="mb-6 text-left">
              <p className="text-sm font-semibold text-gray-700 mb-2">Aged</p>
              <div className="flex justify-between text-sm text-gray-700 mb-1">
                <span>{ageRange[0]} yrs</span>
                <span>{ageRange[1]} yrs</span>
              </div>
              <ReactSlider
                min={18}
                max={75}
                value={ageRange}
                onChange={setAgeRange}
                pearling
                minDistance={1}
                className="custom-slider"
                thumbClassName="custom-thumb"
                renderTrack={(props, state) => {
                  const colors = ["#d1d5db", "#3b82f6", "#d1d5db"];
                  const { key, style, ...rest } = props;
                  return (
                    <div
                      key={key}
                      {...rest}
                      style={{
                        ...style,
                        height: "4px",
                        backgroundColor: colors[state.index],
                        borderRadius: "4px",
                      }}
                    />
                  );
                }}
              />
            </div>

              {/* Country → State → City */}
            <div className="mb-6 text-left space-y-3">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  Country
                </p>
                <select
                  className="w-full border border-gray-300 px-3 py-2 rounded"
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                >
                  <option value="">Any Country</option>
                  {countries.map((c) => (
                    <option key={c.isoCode} value={c.isoCode}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  State / Region
                </p>
                <select
                  className="w-full border border-gray-300 px-3 py-2 rounded"
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  disabled={!selectedCountry}
                >
                  <option value="">Any State</option>
                  {states.map((s) => (
                    <option key={s.isoCode} value={s.isoCode}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  City
                </p>
                <select
                  className="w-full border border-gray-300 px-3 py-2 rounded"
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  disabled={!selectedState}
                >
                  <option value="">Any City</option>
                  {cities.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              className="bg-blue-600 text-white px-6 py-3 rounded-full w-full"
              onClick={handleSearch}
            >
              Search Singles
            </button>
          </div>
        </section>

        {!isLoggedIn && (
          <section className="py-20 bg-gradient-to-r from-blue-100 to-pink-100 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Let’s find your forever vibe
            </h2>
            <button
              className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-full text-lg"
              onClick={() => {
                navigate("/signup");
                scrollToTop();
              }}
            >
              Create Your Profile
            </button>
          </section>
        )}
      </div>
    </>
  );
}

const Selector = ({ label, options, selected, onSelect }) => (
  <div className="mb-4 text-left">
    <p className="text-sm font-semibold text-gray-700 mb-1">{label}</p>
    <div className="flex gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          onClick={() => onSelect(opt)}
          className={`px-4 py-2 rounded w-full ${
            selected === opt
              ? "bg-blue-600 text-white"
              : "bg-white border border-gray-300"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

const Feature = ({ icon, title, desc }) => (
  <div className="text-center">
    <div className="text-5xl mb-4">{icon}</div>
    <h3 className="font-semibold text-xl mb-2">{title}</h3>
    <p className="text-gray-600 text-sm">{desc}</p>
  </div>
);